import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SchemaMarkupProps {
  type: "LocalBusiness" | "Organization" | "Service";
}

export const SchemaMarkup = ({ type }: SchemaMarkupProps) => {
  // Fetch reviews for schema markup
  const { data: reviews } = useQuery({
    queryKey: ["reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .eq("publish", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    // Remove any existing schema markup
    const existingScript = document.getElementById("schema-markup");
    if (existingScript) {
      existingScript.remove();
    }

    let schema = {};

    if (type === "LocalBusiness") {
      // Calculate aggregate rating
      const aggregateRating = reviews?.length
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
        : "5.0";

      // Build individual review schemas
      const reviewSchemas = reviews?.map((review) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.reviewer_name,
        },
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.rating.toString(),
          bestRating: "5",
        },
        reviewBody: review.excerpt,
        datePublished: review.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
      })) || [];

      schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://www.shoresightmarine.com/#business",
        name: "ShoreSight Marine",
        legalName: "ShoreSight Marine Windows LLC",
        image: "https://www.shoresightmarine.com/images/shoresight-logo-light.png",
        telephone: "+1-619-377-4673",
        email: "info@shoresightmarine.com",
        address: {
          "@type": "PostalAddress",
          streetAddress: "2608 Shelter Island Drive, Suite 302",
          addressLocality: "San Diego",
          addressRegion: "CA",
          postalCode: "92106",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 32.7157,
          longitude: -117.1611,
        },
        url: "https://www.shoresightmarine.com",
        sameAs: [
          "https://www.instagram.com/shoresightmarine",
        ],
        priceRange: "$$",
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "16:00",
          },
        ],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: aggregateRating,
          reviewCount: reviews?.length?.toString() || "50",
          bestRating: "5",
          worstRating: "1",
        },
        review: reviewSchemas,
        description:
          "Professional marine window installation, repair, and restoration services in San Diego. Specializing in yacht windows, hatches, and portlights with expert craftsmanship.",
        areaServed: {
          "@type": "GeoCircle",
          geoMidpoint: {
            "@type": "GeoCoordinates",
            latitude: 32.7157,
            longitude: -117.1611,
          },
          geoRadius: "50000",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Marine Window Services",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Marine Window Installation",
                description: "Professional installation of yacht and boat windows",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Marine Window Repair",
                description: "Expert repair services for damaged marine windows",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Marine Window Replacement",
                description: "Complete window replacement with premium materials",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Hatch & Portlight Services",
                description: "Installation and repair of marine hatches and portlights",
              },
            },
          ],
        },
      };
    }

    // Create and append script tag
    const script = document.createElement("script");
    script.id = "schema-markup";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById("schema-markup");
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, reviews]);

  return null;
};
